import { styled } from '@attentive-platform/stem-ui';

import { CHIP_TONES } from './constants';

// -----------------------------------------------------------------------------
// StateLozenge
// -----------------------------------------------------------------------------

const stateLozengeBase = styled('span')(() => ({
  display: 'inline-flex',
  alignItems: 'center',
  fontSize: '0.875rem',
  fontWeight: 500,
  padding: '2px 10px',
  borderRadius: '999px'
}));

const toneStyles = tone => {
  const t = CHIP_TONES[tone] || CHIP_TONES.neutral;
  return {
    color: t.color,
    background: t.background,
    border: `1px solid ${t.border}`
  };
};

export const StateLozengeStyles = {
  Primary: styled(stateLozengeBase)(() => toneStyles('primary')),
  Success: styled(stateLozengeBase)(() => toneStyles('success')),
  Warning: styled(stateLozengeBase)(() => toneStyles('warning')),
  Danger: styled(stateLozengeBase)(() => toneStyles('danger')),
  Neutral: styled(stateLozengeBase)(() => toneStyles('neutral')),
  Muted: styled(stateLozengeBase)(() => toneStyles('muted'))
};

/** Shared status pill for type / link / outcome columns. */
export const StatusChip = styled('span', { shouldForwardProp: p => p !== 'tone' })(({ tone }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  fontSize: '0.875rem',
  fontWeight: 500,
  padding: '2px 8px',
  borderRadius: '999px',
  whiteSpace: 'nowrap',
  ...toneStyles(tone)
}));

// -----------------------------------------------------------------------------
// Root (index.jsx)
// -----------------------------------------------------------------------------

export const RootStyles = {
  Page: styled('div')(() => ({
    minHeight: '100vh',
    background: '#FFFFFF'
  })),
  TabsBar: styled('div')(() => ({
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '0 24px',
    borderBottom: '1px solid #EBEBEB',
    overflowX: 'auto'
  }))
};

// -----------------------------------------------------------------------------
// CatalogTab
// -----------------------------------------------------------------------------

export const CatalogTabStyles = {
  Wrap: styled('div')(() => ({
    padding: '20px 24px 40px',
    display: 'flex',
    flexDirection: 'column',
    gap: '14px'
  })),
  Header: styled('div')(() => ({
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: '16px',
    flexWrap: 'wrap'
  })),
  HeaderText: styled('div')(() => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '3px',
    flex: '1 1 280px',
    minWidth: '240px'
  })),
  HeaderControls: styled('div')(() => ({
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    flex: 'none',
    flexWrap: 'wrap'
  })),
  SearchWrap: styled('div')(() => ({ width: '270px' })),
  LensWrap: styled('div')(() => ({ width: '230px' })),
  ModeToggle: styled('div')(() => ({
    display: 'flex',
    gap: '4px',
    border: '1px solid #EBEBEB',
    borderRadius: '8px',
    padding: '3px',
    background: '#FAFAFA'
  })),
  ModeChipOn: styled('span')(() => ({
    fontSize: '12.5px',
    fontWeight: 500,
    color: '#112FBB',
    background: '#FFFFFF',
    borderRadius: '6px',
    padding: '5px 12px',
    cursor: 'pointer',
    whiteSpace: 'nowrap'
  })),
  ModeChipOff: styled('span')(() => ({
    fontSize: '12.5px',
    fontWeight: 500,
    color: '#4D4D4D',
    background: 'transparent',
    borderRadius: '6px',
    padding: '5px 12px',
    cursor: 'pointer',
    whiteSpace: 'nowrap'
  })),
  LensBanner: styled('div')(() => ({
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    background: '#FBFCFF',
    border: '1px solid #B9C5F8',
    borderRadius: '10px',
    padding: '11px 15px',
    flexWrap: 'wrap'
  })),
  LensSummary: styled('span')(() => ({
    fontSize: '13px',
    color: '#112FBB',
    fontWeight: 500
  })),
  LensHint: styled('span')(() => ({
    fontSize: '12.5px',
    color: '#4D4D4D'
  })),
  LensClear: styled('span')(() => ({
    fontSize: '12.5px',
    color: '#4361EE',
    cursor: 'pointer',
    marginLeft: 'auto',
    whiteSpace: 'nowrap'
  })),

  Split: styled('div')(() => ({
    display: 'grid',
    gridTemplateColumns: '248px minmax(0, 1fr)',
    gap: '16px',
    alignItems: 'start'
  })),

  Rail: styled('div')(() => ({
    position: 'sticky',
    top: '16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    maxHeight: 'calc(100vh - 60px)',
    overflow: 'auto'
  })),
  RailHead: styled('div')(() => ({
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: '10px',
    padding: '0 10px 8px'
  })),
  RailHeadLabel: styled('span')(() => ({
    fontSize: '12px',
    color: '#808080',
    letterSpacing: '.03em',
    textTransform: 'uppercase'
  })),
  RailHeadHits: styled('span')(() => ({
    fontSize: '12px',
    color: '#9E9E9E'
  })),
  RailTradeWrap: styled('div')(() => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '2px'
  })),
  RailTradeRow: styled('div')(({ active }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '9px 10px',
    borderRadius: '8px',
    cursor: 'pointer',
    background: active ? '#F6F7FE' : 'transparent',
    borderLeft: `3px solid ${active ? '#4361EE' : 'transparent'}`,
    '&:hover': { background: '#F6F7FE' }
  })),
  RailTradeName: styled('span')(({ active }) => ({
    fontSize: '13.5px',
    fontWeight: active ? 600 : 500,
    color: active ? '#112FBB' : '#1A1A1A',
    flex: 1,
    minWidth: 0,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  })),
  RailTradeCount: styled('span')(() => ({
    fontSize: '12px',
    color: '#9E9E9E',
    whiteSpace: 'nowrap',
    flex: 'none'
  })),
  RailSectionList: styled('div')(() => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '1px',
    padding: '2px 0 8px 16px',
    borderLeft: '1px solid #EBEBEB',
    marginLeft: '12px'
  })),
  RailSectionRow: styled('div')(() => ({
    display: 'flex',
    alignItems: 'center',
    gap: '9px',
    padding: '6px 10px',
    borderRadius: '7px'
  })),
  RailSectionName: styled('span')(() => ({
    fontSize: '12.5px',
    lineHeight: 1.35,
    color: '#4D4D4D',
    flex: 1,
    minWidth: 0
  })),
  RailSectionCount: styled('span')(() => ({
    fontSize: '11.5px',
    color: '#9E9E9E',
    flex: 'none'
  })),

  Panel: styled('div')(() => ({
    border: '1px solid #EBEBEB',
    borderRadius: '12px',
    background: '#FFFFFF',
    overflow: 'hidden'
  })),
  PanelHead: styled('div')(() => ({
    padding: '16px 18px',
    borderBottom: '1px solid #F0F0F0',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px'
  })),
  PanelTitleRow: styled('div')(() => ({
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    flexWrap: 'wrap'
  })),
  PanelTitle: styled('span')(() => ({
    fontSize: '16px',
    fontWeight: 600
  })),
  PanelCountLabel: styled('span')(() => ({
    fontSize: '12.5px',
    color: '#808080'
  })),
  PanelDescription: styled('span')(() => ({
    fontSize: '13px',
    color: '#666666',
    lineHeight: 1.55,
    maxWidth: '76ch'
  })),

  SectionBlock: styled('div')(() => ({
    padding: '11px 18px 5px',
    display: 'flex',
    flexDirection: 'column',
    gap: '2px'
  })),
  SectionHead: styled('div')(() => ({
    display: 'flex',
    alignItems: 'baseline',
    gap: '9px',
    paddingBottom: '3px'
  })),
  SectionEyebrow: styled('span')(() => ({
    fontSize: '11px',
    color: '#9E9E9E',
    letterSpacing: '.04em',
    textTransform: 'uppercase'
  })),
  SectionCount: styled('span')(() => ({
    fontSize: '11px',
    color: '#C4C4C4'
  })),

  ItemRow: styled('div', { shouldForwardProp: p => p !== 'dimmed' })(({ dimmed }) => ({
    borderTop: '1px solid #F5F5F5',
    padding: '9px 0',
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    opacity: dimmed ? 0.55 : 1
  })),
  ItemTop: styled('div')(() => ({
    display: 'flex',
    alignItems: 'baseline',
    gap: '12px'
  })),
  ItemLabelWrap: styled('div')(() => ({
    flex: 1,
    minWidth: 0,
    display: 'flex',
    alignItems: 'baseline',
    gap: '10px',
    flexWrap: 'wrap',
    cursor: 'pointer'
  })),
  ItemChevron: styled('span')(() => ({
    fontSize: '9.5px',
    color: '#BDBDBD',
    flex: 'none'
  })),
  ItemLabel: styled('span')(() => ({
    fontSize: '13.5px',
    fontWeight: 500,
    color: '#1A1A1A'
  })),
  ItemSummary: styled('span')(() => ({
    fontSize: '12.5px',
    color: '#666666',
    whiteSpace: 'nowrap',
    cursor: 'pointer'
  })),
  ItemInfo: styled('span')(() => ({
    flex: 'none',
    width: '18px',
    height: '18px',
    borderRadius: '9px',
    border: '1px solid #DDE1E8',
    color: '#808080',
    fontSize: '11px',
    fontWeight: 500,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    fontStyle: 'italic'
  })),
  AdvancedPill: styled('span')(() => ({
    fontSize: '11px',
    color: '#4D4D4D',
    background: '#F5F5F5',
    border: '1px solid #EBEBEB',
    borderRadius: '999px',
    padding: '1px 8px',
    whiteSpace: 'nowrap'
  })),

  ItemDetail: styled('div')(() => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '13px',
    padding: '2px 0 6px 20px'
  })),
  DetailBlock: styled('div')(() => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '6px'
  })),
  DetailEyebrow: styled('span')(() => ({
    fontSize: '11px',
    color: '#9E9E9E',
    letterSpacing: '.04em',
    textTransform: 'uppercase'
  })),
  ElementRow: styled('div')(() => ({
    display: 'flex',
    flexWrap: 'wrap',
    gap: '5px'
  })),
  ElementTag: styled('span', { shouldForwardProp: p => p !== 'picked' && p !== 'dimmed' })(({ picked, dimmed }) => ({
    fontSize: '12px',
    color: picked ? '#0F7A2E' : dimmed ? '#9E9E9E' : '#112FBB',
    background: picked ? '#E6F5EA' : dimmed ? '#F5F5F5' : '#EDF0FE',
    border: `1px solid ${picked ? '#B7DFC0' : dimmed ? '#EBEBEB' : '#B9C5F8'}`,
    borderRadius: '999px',
    padding: '3px 10px',
    whiteSpace: 'nowrap'
  })),
  ModifierRow: styled('div')(() => ({
    display: 'flex',
    alignItems: 'baseline',
    gap: '9px',
    flexWrap: 'wrap'
  })),
  KindPill: styled('span')(() => ({
    fontSize: '11px',
    color: '#808080',
    background: '#FAFAFA',
    border: '1px solid #EBEBEB',
    borderRadius: '4px',
    padding: '1px 7px',
    whiteSpace: 'nowrap'
  })),
  ModifierBody: styled('span')(() => ({
    fontSize: '12.5px',
    color: '#666666',
    lineHeight: 1.5,
    maxWidth: '72ch'
  })),
  InputDefault: styled('span')(() => ({
    fontSize: '12.5px',
    color: '#4D4D4D',
    border: '1px dashed #DDE1E8',
    borderRadius: '6px',
    padding: '5px 10px',
    alignSelf: 'flex-start'
  })),
  OptionRow: styled('div')(() => ({
    display: 'flex',
    flexWrap: 'wrap',
    gap: '5px'
  })),
  OptionChip: styled('span')(() => ({
    fontSize: '12px',
    color: '#4D4D4D',
    background: '#FFFFFF',
    border: '1px solid #EBEBEB',
    borderRadius: '6px',
    padding: '4px 10px'
  })),

  LoadingBox: styled('div')(() => ({
    padding: '48px 24px',
    textAlign: 'center',
    fontSize: '13px',
    color: '#808080'
  })),
  EmptyBox: styled('div')(() => ({
    padding: '32px 20px',
    textAlign: 'center',
    fontSize: '13px',
    color: '#808080'
  }))
};

// -----------------------------------------------------------------------------
// Shared table primitives (IntakeTab + RecordsTab share the same layout DNA)
// -----------------------------------------------------------------------------

export const TableStyles = {
  Wrap: styled('div')(() => ({
    padding: '20px 24px 40px'
  })),
  Toolbar: styled('div')(() => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '16px',
    paddingBottom: '16px',
    flexWrap: 'wrap'
  })),
  ToolbarLeft: styled('div')(() => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '3px'
  })),
  Filters: styled('div')(() => ({
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    flex: 'none'
  })),
  TableBox: styled('div')(() => ({
    border: '1px solid #EBEBEB',
    borderRadius: '10px',
    overflowX: 'auto'
  })),
  SortHeader: styled('button')(() => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '12px 14px',
    margin: 0,
    border: 'none',
    background: 'transparent',
    font: 'inherit',
    fontSize: '0.875rem',
    fontWeight: 600,
    color: '#4D4D4D',
    whiteSpace: 'nowrap',
    cursor: 'pointer',
    textAlign: 'left',
    width: '100%',
    '&:hover': { color: '#163CE9' }
  })),
  SortMark: styled('span')(() => ({
    fontSize: '10px',
    color: '#9E9E9E',
    fontWeight: 500
  })),
  Cell: styled('div')(() => ({
    padding: '13px 14px'
  })),
  CellCol: styled('div')(() => ({
    padding: '13px 14px',
    display: 'flex',
    flexDirection: 'column',
    gap: '3px'
  })),
  CellStack: styled('div')(() => ({
    padding: '13px 14px',
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    minWidth: 0,
    overflow: 'hidden'
  })),
  EmptyBig: styled('div')(() => ({
    padding: '64px 24px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '10px'
  })),
  EmptySmall: styled('div')(() => ({
    padding: '48px 24px',
    textAlign: 'center',
    fontSize: '13px',
    color: '#808080'
  })),
  FooterRow: styled('div')(() => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingTop: '14px'
  })),
  FooterRight: styled('div')(() => ({
    display: 'flex',
    alignItems: 'center',
    gap: '16px'
  })),
  SearchWide: styled('div')(() => ({
    width: '380px',
    flex: 'none'
  })),
  SearchNarrow: styled('div')(() => ({
    width: '320px',
    flex: 'none'
  })),
  SelectSmall: styled('div')(() => ({
    width: '150px'
  })),
  SelectMedium: styled('div')(() => ({
    width: '180px'
  })),
  SelectWide: styled('div')(() => ({
    width: '170px'
  }))
};

// -----------------------------------------------------------------------------
// IntakeTab
// -----------------------------------------------------------------------------

const intakeGridColumns =
  '200px 150px 140px 110px 90px 70px 130px 140px 150px 150px 130px 130px 110px 120px';
const intakeGridMin = '1820px';

export const IntakeTabStyles = {
  HeaderGrid: styled('div')(() => ({
    display: 'grid',
    gridTemplateColumns: intakeGridColumns,
    minWidth: intakeGridMin,
    background: '#FAFAFA',
    borderBottom: '1px solid #EBEBEB'
  })),
  RowOpen: styled('div')(() => ({
    display: 'grid',
    gridTemplateColumns: intakeGridColumns,
    minWidth: intakeGridMin,
    borderBottom: '1px solid #F5F5F5',
    background: '#FFFFFF',
    cursor: 'pointer'
  })),
  RowClosed: styled('div')(() => ({
    display: 'grid',
    gridTemplateColumns: intakeGridColumns,
    minWidth: intakeGridMin,
    borderBottom: '1px solid #F5F5F5',
    background: '#FCFCFD',
    cursor: 'pointer'
  })),
  EmailText: styled('span')(() => ({
    fontSize: '0.875rem',
    fontWeight: 500,
    color: '#163CE9',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  })),
  MismatchPill: styled('span')(() => ({
    fontSize: '0.75rem',
    color: '#995C00',
    background: '#FFF5E5',
    border: '1px solid #FFEACC',
    borderRadius: '4px',
    padding: '1px 6px',
    alignSelf: 'flex-start'
  })),
  CellTrunc: styled('div')(() => ({
    padding: '13px 14px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  })),
  AgingStale: styled('span')(() => ({
    fontSize: '0.875rem',
    color: '#995C00',
    fontWeight: 600
  })),
  AgingCalm: styled('span')(() => ({
    fontSize: '0.875rem',
    color: '#4D4D4D',
    fontWeight: 400
  })),
  EmptyCtaWrap: styled('div')(() => ({
    marginTop: '6px'
  })),
  TradesCount: styled('button')(() => ({
    border: 'none',
    background: 'transparent',
    padding: 0,
    margin: 0,
    font: 'inherit',
    fontSize: '0.875rem',
    fontWeight: 500,
    color: '#163CE9',
    cursor: 'pointer',
    textAlign: 'left'
  })),
  TradesExpanded: styled('div')(() => ({
    marginTop: '4px',
    fontSize: '0.75rem',
    color: '#666666',
    lineHeight: 1.45
  })),
  EmailsOff: styled('span')(() => ({
    fontSize: '0.875rem',
    color: '#9E9E9E'
  }))
};

// -----------------------------------------------------------------------------
// RecordsTab
// -----------------------------------------------------------------------------

const recordsGridColumns = '180px 160px 130px 190px 110px 130px 130px 170px 130px';
const recordsGridMin = '1330px';

export const RecordsTabStyles = {
  HeaderGrid: styled('div')(() => ({
    display: 'grid',
    gridTemplateColumns: recordsGridColumns,
    minWidth: recordsGridMin,
    background: '#FAFAFA',
    borderBottom: '1px solid #EBEBEB'
  })),
  RowOpen: styled('div')(() => ({
    display: 'grid',
    gridTemplateColumns: recordsGridColumns,
    minWidth: recordsGridMin,
    borderBottom: '1px solid #F5F5F5',
    background: '#FFFFFF',
    cursor: 'pointer'
  })),
  RowClosed: styled('div')(() => ({
    display: 'grid',
    gridTemplateColumns: recordsGridColumns,
    minWidth: recordsGridMin,
    borderBottom: '1px solid #F5F5F5',
    background: '#FCFCFD',
    cursor: 'pointer'
  })),
  CompanyText: styled('div')(() => ({
    padding: '13px 14px',
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    minWidth: 0
  })),
  CompanyName: styled('span')(() => ({
    fontSize: '0.875rem',
    fontWeight: 500,
    color: '#163CE9',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  })),
  AdminEmail: styled('span')(() => ({
    fontSize: '0.75rem',
    color: '#4D4D4D',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  })),
  ProspectLine: styled('span')(() => ({
    fontSize: '0.75rem',
    color: '#808080',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  })),
  VersionDisplay: styled('span')(() => ({
    fontSize: '0.875rem',
    fontWeight: 500,
    color: '#1A1A1A',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '100%',
    display: 'block',
    cursor: 'default'
  })),
  TradesCount: styled('button')(() => ({
    border: 'none',
    background: 'transparent',
    padding: 0,
    margin: 0,
    font: 'inherit',
    fontSize: '13.5px',
    fontWeight: 500,
    color: '#163CE9',
    cursor: 'pointer',
    textAlign: 'left'
  })),
  TradesExpanded: styled('div')(() => ({
    marginTop: '4px',
    fontSize: '12.5px',
    color: '#666666',
    lineHeight: 1.45
  }))
};

// -----------------------------------------------------------------------------
// IntakeDrawer
// -----------------------------------------------------------------------------

export const IntakeDrawerStyles = {
  Backdrop: styled('div')(() => ({
    position: 'fixed',
    inset: 0,
    background: 'rgba(4,12,47,.28)',
    zIndex: 60
  })),
  Drawer: styled('div')(() => ({
    position: 'fixed',
    top: 0,
    right: 0,
    bottom: 0,
    width: '560px',
    background: '#FFFFFF',
    boxShadow: '-12px 0 40px rgba(9,24,93,.16)',
    zIndex: 61,
    display: 'flex',
    flexDirection: 'column'
  })),
  Header: styled('div')(() => ({
    padding: '20px 24px',
    borderBottom: '1px solid #EBEBEB',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  })),
  Close: styled('span')(() => ({
    fontSize: '20px',
    color: '#808080',
    cursor: 'pointer',
    lineHeight: 1
  })),
  Body: styled('div')(() => ({
    padding: '20px 24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    overflow: 'auto',
    flex: 1
  })),
  Field: styled('div')(() => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '6px'
  })),
  FieldPair: styled('div')(() => ({
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '12px'
  })),
  BlockedCard: styled('div')(() => ({
    border: '1px solid #FFEACC',
    background: '#FFFBF5',
    borderRadius: '10px',
    padding: '14px',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px'
  })),
  BlockedInner: styled('div')(() => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    background: '#FFFFFF',
    border: '1px solid #F0F0F0',
    borderRadius: '8px',
    padding: '11px 13px'
  })),
  BlockedInnerLeft: styled('div')(() => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '2px'
  })),
  BlockedActions: styled('div')(() => ({
    display: 'flex',
    gap: '8px'
  })),
  ChipRow: styled('div')(() => ({
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
    paddingTop: '2px'
  })),
  ChipValid: styled('span')(() => ({
    fontSize: '12px',
    padding: '2px 10px',
    borderRadius: '999px',
    color: '#4D4D4D',
    background: '#F5F5F5',
    border: '1px solid #EBEBEB'
  })),
  ChipInvalid: styled('span')(() => ({
    fontSize: '12px',
    padding: '2px 10px',
    borderRadius: '999px',
    color: '#C1180B',
    background: '#FFF0EE',
    border: '1px solid #F8C4BF'
  })),
  Footer: styled('div')(() => ({
    padding: '16px 24px',
    borderTop: '1px solid #EBEBEB',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px'
  })),
  FooterActions: styled('div')(() => ({
    display: 'flex',
    gap: '8px'
  })),
  FieldErrorText: styled('span')(() => ({
    fontSize: '12.5px',
    color: '#C1180B'
  })),
  CcHint: styled('span')(() => ({
    fontSize: '12.5px',
    color: '#808080'
  })),
  CcHintError: styled('span')(() => ({
    fontSize: '12.5px',
    color: '#C1180B'
  })),
  EmailsToggleRow: styled('div')(() => ({
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: '14px',
    padding: '12px 14px',
    borderRadius: '10px',
    border: '1px solid #EBEBEB',
    background: '#FAFAFA'
  })),
  EmailsToggleCopy: styled('div')(() => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '3px',
    minWidth: 0
  })),
  EmailsToggleTitle: styled('span')(() => ({
    fontSize: '13.5px',
    fontWeight: 500,
    color: '#1A1A1A'
  })),
  EmailsToggleHint: styled('span')(() => ({
    fontSize: '12.5px',
    lineHeight: 1.45,
    color: '#808080'
  })),
  TypeStack: styled('div')(() => ({
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '10px'
  })),
  TypeOn: styled('button')(() => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '4px',
    textAlign: 'left',
    padding: '12px 14px',
    borderRadius: '10px',
    border: '1px solid #B9C5F8',
    background: '#F6F7FE',
    boxShadow: '0 0 0 1px #B9C5F8',
    cursor: 'pointer',
    font: 'inherit'
  })),
  TypeOff: styled('button')(() => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '4px',
    textAlign: 'left',
    padding: '12px 14px',
    borderRadius: '10px',
    border: '1px solid #EBEBEB',
    background: '#FFFFFF',
    cursor: 'pointer',
    font: 'inherit',
    '&:hover': {
      borderColor: '#D0D8FB',
      background: '#FAFBFF'
    }
  })),
  TypeTitle: styled('span')(() => ({
    fontSize: '14px',
    fontWeight: 600,
    color: '#1A1A1A'
  })),
  TypeBody: styled('span')(() => ({
    fontSize: '12.5px',
    lineHeight: 1.45,
    color: '#666666'
  }))
};

// -----------------------------------------------------------------------------
// FillOnBehalfView
// -----------------------------------------------------------------------------

export const FillOnBehalfViewStyles = {
  Root: styled('div')(() => ({
    minHeight: 'calc(100vh - 64px)',
    background: '#FFFFFF'
  })),
  LoadingWrap: styled('div')(() => ({
    padding: '48px 24px',
    textAlign: 'center'
  })),
  Ribbon: styled('div')(() => ({
    background: '#F6F7FE',
    borderBottom: '1px solid #D0D8FB',
    padding: '14px 24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '20px',
    position: 'sticky',
    top: 0,
    zIndex: 20
  })),
  RibbonLeft: styled('div')(() => ({
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
    flexWrap: 'wrap'
  })),
  Pill: styled('span')(() => ({
    fontSize: '11px',
    fontWeight: 600,
    letterSpacing: '.05em',
    textTransform: 'uppercase',
    color: '#112FBB',
    background: '#FFFFFF',
    border: '1px solid #D0D8FB',
    borderRadius: '999px',
    padding: '3px 10px'
  })),
  Actions: styled('div')(() => ({
    display: 'flex',
    gap: '8px'
  })),
  InternalNoteBlock: styled('div')(() => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    marginBottom: '16px'
  })),
  InternalNoteLabel: styled('span')(() => ({
    fontSize: '13px',
    fontWeight: 600,
    color: '#1A1A1A'
  })),
  InternalNoteHint: styled('span')(() => ({
    fontSize: '12.5px',
    color: '#666666',
    marginBottom: '4px'
  })),
  RequiredMark: styled('span')(() => ({
    color: '#C1180B',
    fontWeight: 600
  })),
  InternalNoteError: styled('span')(() => ({
    fontSize: '12.5px',
    color: '#C1180B'
  }))
};

// -----------------------------------------------------------------------------
// DetailView
// -----------------------------------------------------------------------------

export const DetailViewStyles = {
  LoadingBox: styled('div')(() => ({
    padding: '64px 24px',
    textAlign: 'center',
    color: '#808080'
  })),
  ErrorWrap: styled('div')(() => ({
    padding: '48px 24px'
  })),
  BackLink: styled('span')(() => ({
    fontSize: '13px',
    color: '#4361EE',
    cursor: 'pointer'
  })),
  ErrorHint: styled('div')(() => ({
    marginTop: '16px',
    fontSize: '13.5px',
    color: '#666666'
  })),
  Wrap: styled('div')(() => ({
    padding: '20px 24px 60px'
  })),
  Header: styled('div')(() => ({
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: '24px',
    padding: '16px 0 20px',
    borderBottom: '1px solid #EBEBEB',
    marginTop: '12px'
  })),
  HeaderLeft: styled('div')(() => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    minWidth: 0
  })),
  HeaderTitleRow: styled('div')(() => ({
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flexWrap: 'wrap'
  })),
  H1: styled('h1')(() => ({
    margin: 0,
    fontSize: '24px',
    fontWeight: 600,
    letterSpacing: '-0.01em'
  })),
  PilotChip: styled('span', { shouldForwardProp: p => p !== 'tone' && p !== 'live' })(({ tone, live }) => {
    const resolved = tone || (live ? 'success' : 'neutral');
    const t = CHIP_TONES[resolved] || CHIP_TONES.neutral;
    return {
      fontSize: '0.875rem',
      fontWeight: 500,
      color: t.color,
      border: `1px solid ${t.border}`,
      background: t.background,
      borderRadius: '999px',
      padding: '4px 12px',
      whiteSpace: 'nowrap'
    };
  }),
  MetaRow: styled('div')(() => ({
    display: 'flex',
    gap: '22px',
    flexWrap: 'wrap'
  })),
  MetaCol: styled('div')(() => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '2px'
  })),
  MetaLabel: styled('span')(() => ({
    fontSize: '11.5px',
    color: '#808080',
    letterSpacing: '.03em',
    textTransform: 'uppercase'
  })),
  MetaValue: styled('span')(() => ({
    fontSize: '13.5px',
    fontWeight: 500
  })),
  HeaderActions: styled('div')(() => ({
    display: 'flex',
    gap: '8px',
    flex: 'none',
    flexWrap: 'wrap',
    justifyContent: 'flex-end'
  })),
  BodyGrid: styled('div')(() => ({
    display: 'grid',
    gridTemplateColumns: '360px minmax(0,1fr)',
    gap: '32px',
    paddingTop: '20px',
    alignItems: 'start'
  })),
  Sidebar: styled('div')(() => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    position: 'sticky',
    top: '20px'
  })),
  SidebarLabel: styled('span')(() => ({
    fontSize: '11.5px',
    color: '#808080',
    letterSpacing: '.03em',
    textTransform: 'uppercase'
  })),
  VersionCardOn: styled('div')(() => ({
    border: '1px solid #B9C5F8',
    background: '#F6F7FE',
    borderRadius: '10px',
    padding: '12px 14px',
    display: 'flex',
    flexDirection: 'column',
    gap: '3px',
    minWidth: 0,
    overflow: 'hidden'
  })),
  VersionTopRow: styled('div')(() => ({
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  })),
  VersionMetaRow: styled('div')(() => ({
    display: 'flex',
    alignItems: 'baseline',
    gap: '4px',
    minWidth: 0,
    fontSize: '12px',
    lineHeight: 1.4,
    color: '#808080'
  })),
  VersionEmail: styled('span')(() => ({
    flex: '1 1 0',
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    cursor: 'default'
  })),
  VersionMetaDate: styled('span')(() => ({
    flex: 'none',
    whiteSpace: 'nowrap'
  })),
  VersionBehalfLine: styled('div')(() => ({
    display: 'flex',
    alignItems: 'baseline',
    gap: '4px',
    minWidth: 0,
    fontSize: '12px',
    lineHeight: 1.4,
    color: '#4D4D4D'
  })),
  VersionBehalfPrefix: styled('span')(() => ({
    flex: 'none',
    whiteSpace: 'nowrap'
  })),
  CurrentPill: styled('span')(() => ({
    fontSize: '11px',
    color: '#112FBB',
    background: '#E3E8FC',
    borderRadius: '999px',
    padding: '1px 8px'
  })),
  SidebarSection: styled('div')(() => ({
    borderTop: '1px solid #F0F0F0',
    marginTop: '2px',
    paddingTop: '12px',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  })),
  SidebarSectionHeader: styled('div')(() => ({
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: '10px'
  })),
  SidebarEditLink: styled('span')(() => ({
    fontSize: '12.5px',
    fontWeight: 500,
    color: '#163CE9',
    cursor: 'pointer',
    whiteSpace: 'nowrap'
  })),
  AttrEdit: styled('div')(() => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    border: '1px solid #D0D8FB',
    background: '#FBFCFF',
    borderRadius: '10px',
    padding: '12px'
  })),
  AttrEditField: styled('div')(() => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '4px'
  })),
  AttrEditActions: styled('div')(() => ({
    display: 'flex',
    gap: '8px',
    paddingTop: '2px'
  })),
  AttrReadRow: styled('div')(() => ({
    display: 'flex',
    justifyContent: 'space-between',
    gap: '12px'
  })),
  EmailsToggleRow: styled('div')(() => ({
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: '12px',
    padding: '12px 14px',
    borderRadius: '10px',
    border: '1px solid #EBEBEB',
    background: '#FAFAFA'
  })),
  EmailsToggleCopy: styled('div')(() => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '3px',
    minWidth: 0
  })),
  EmailsToggleTitle: styled('span')(() => ({
    fontSize: '13.5px',
    fontWeight: 500,
    color: '#1A1A1A'
  })),
  EmailsToggleHint: styled('span')(() => ({
    fontSize: '12.5px',
    lineHeight: 1.45,
    color: '#808080'
  })),
  EmailsToggleNote: styled('span')(() => ({
    fontSize: '12.5px',
    lineHeight: 1.45,
    color: '#666666',
    marginTop: '2px'
  })),
  ChangeLogList: styled('div')(() => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '10px'
  })),
  ChangeLogItem: styled('div')(() => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '2px'
  })),
  Main: styled('div')(() => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '14px'
  })),
  WaitingCard: styled('div')(() => ({
    border: '1px solid #EBEBEB',
    borderRadius: '12px',
    padding: '26px 28px',
    display: 'flex',
    flexDirection: 'column',
    gap: '18px'
  })),
  WaitingTitleBlock: styled('div')(() => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '6px'
  })),
  WaitingStats: styled('div')(() => ({
    display: 'grid',
    gridTemplateColumns: 'repeat(2,minmax(0,1fr))',
    gap: '12px',
    maxWidth: '520px'
  })),
  WaitingStatCard: styled('div')(() => ({
    background: '#FAFAFA',
    border: '1px solid #F0F0F0',
    borderRadius: '10px',
    padding: '14px 16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '3px'
  })),
  ScopeToolbar: styled('div')(() => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '16px',
    borderBottom: '1px solid #EBEBEB',
    flexWrap: 'wrap'
  })),
  ScopeTab: styled('span')(() => ({
    fontSize: '13px',
    fontWeight: 500,
    color: '#163CE9',
    padding: '9px 11px',
    borderBottom: '2px solid #4361EE',
    whiteSpace: 'nowrap'
  })),
  ViewToggleWrap: styled('div')(() => ({
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    paddingBottom: '6px'
  })),
  ViewToggleGroup: styled('div')(() => ({
    display: 'flex',
    gap: '4px',
    border: '1px solid #EBEBEB',
    borderRadius: '8px',
    padding: '3px',
    background: '#FAFAFA'
  })),
  ViewToggle: styled('span', { shouldForwardProp: p => p !== 'on' })(({ on }) => ({
    fontSize: '12.5px',
    fontWeight: 500,
    color: on ? '#163CE9' : '#666666',
    background: on ? '#FFFFFF' : 'transparent',
    borderRadius: '6px',
    padding: '5px 12px',
    cursor: 'pointer',
    userSelect: 'none'
  })),
  TradeCard: styled('div')(() => ({
    border: '1px solid #EBEBEB',
    borderRadius: '12px',
    overflow: 'hidden'
  })),
  TradeHeaderOpen: styled('div')(() => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    padding: '12px 16px',
    background: '#FAFAFA',
    borderBottom: '1px solid #EBEBEB',
    cursor: 'pointer'
  })),
  TradeHeaderClosed: styled('div')(() => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    padding: '12px 16px',
    background: '#FFFFFF',
    borderBottom: '1px solid #EBEBEB',
    cursor: 'pointer'
  })),
  TradeHeaderLeft: styled('div')(() => ({
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    minWidth: 0
  })),
  TradeScopeBlock: styled('div')(() => ({
    padding: '10px 16px 4px',
    display: 'flex',
    flexDirection: 'column',
    gap: '2px'
  })),
  ScopeRow: styled('div')(() => ({
    borderTop: '1px solid #F5F5F5',
    padding: '9px 0',
    display: 'flex',
    flexDirection: 'column',
    gap: '6px'
  })),
  ScopeRowClickable: styled('div')(() => ({
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    cursor: 'pointer'
  })),
  ScopeRowStatic: styled('div')(() => ({
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    cursor: 'default'
  })),
  ScopeRowText: styled('div')(() => ({
    flex: 1,
    minWidth: 0,
    display: 'flex',
    alignItems: 'baseline',
    gap: '10px',
    flexWrap: 'wrap'
  })),
  ScopeExpandHint: styled('span')(() => ({
    fontSize: '10.5px',
    color: '#9E9E9E',
    paddingTop: '3px'
  })),
  ScopeExpanded: styled('div')(() => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '7px'
  })),
  Chips: styled('div')(() => ({
    display: 'flex',
    flexWrap: 'wrap',
    gap: '5px'
  })),
  Chip: styled('span')(() => ({
    fontSize: '11.5px',
    padding: '2px 8px',
    borderRadius: '999px',
    color: '#4D4D4D',
    background: '#F5F5F5',
    border: '1px solid #EBEBEB'
  })),
  KVLine: styled('div')(() => ({
    display: 'flex',
    gap: '7px',
    flexWrap: 'wrap'
  })),
  TradeDetail: styled('div')(() => ({
    padding: '12px 16px 16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '7px',
    background: '#FCFCFD'
  }))
};

// -----------------------------------------------------------------------------
// ReopenModal
// -----------------------------------------------------------------------------

const choiceBase = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: '12px',
  width: '100%',
  textAlign: 'left',
  borderRadius: '10px',
  padding: '14px 16px',
  font: 'inherit',
  cursor: 'pointer',
  background: '#FFFFFF',
  '&:disabled': {
    cursor: 'not-allowed',
    opacity: 0.7,
    background: '#FAFAFA'
  }
};

export const ReopenModalStyles = {
  ContentWrap: styled('div')(() => ({
    padding: '10px',
    width: '520px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px'
  })),
  Heading: styled('span')(() => ({
    fontSize: '19px',
    fontWeight: 600,
    letterSpacing: '-0.005em'
  })),
  ChoiceStack: styled('div')(() => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '10px'
  })),
  ChoiceOn: styled('button')(() => ({
    ...choiceBase,
    border: '1px solid #B9C5F8',
    background: '#F6F7FE',
    boxShadow: '0 0 0 1px #B9C5F8'
  })),
  ChoiceOff: styled('button')(() => ({
    ...choiceBase,
    border: '1px solid #EBEBEB',
    '&:hover:not(:disabled)': {
      borderColor: '#D0D8FB',
      background: '#FAFBFF'
    }
  })),
  ChoiceRadio: styled('span', { shouldForwardProp: p => p !== 'selected' })(({ selected }) => ({
    width: '16px',
    height: '16px',
    borderRadius: '50%',
    flex: 'none',
    marginTop: '2px',
    border: selected ? '5px solid #4361EE' : '1.5px solid #C4C4C4',
    background: '#FFFFFF',
    boxSizing: 'border-box'
  })),
  ChoiceText: styled('div')(() => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    minWidth: 0
  })),
  ChoiceTitle: styled('span')(() => ({
    fontSize: '14px',
    fontWeight: 600,
    color: '#1A1A1A'
  })),
  ChoiceBody: styled('span')(() => ({
    fontSize: '13px',
    lineHeight: 1.5,
    color: '#666666'
  })),
  ReasonBlock: styled('div')(() => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  })),
  Actions: styled('div')(() => ({
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '8px'
  }))
};
